import { Moment } from 'moment';
import naturalCompare from 'natural-compare-lite';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { DEFAULT_DATE_FORMAT } from 'api/models';
import { Project } from 'api/project';
import { ApiTimesheetEntry, TimesheetAPI } from 'api/timesheets';
import { Category } from 'api/timesheets/categories';
import { Form } from 'components/atoms/form';
import { Option } from 'components/atoms/select/types';
import { useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
// Importing from index file was causing some breaking changes, hence imported directly from the hook file
// eslint-disable-next-line import/no-internal-modules
import { useTimesheetConfiguration } from 'queries/timesheets/configuration';
import { getRandomColor } from 'utils/get-random-color';
import { TimesheetAdhocTask } from '../../types';
import {
  apiTimeEntriesToAdhocTask,
  getSearchFilterForDateAndUser,
} from './utils';

export enum View {
  NewActivity = 'new_activity',
  SubmittedLastWeek = 'submitted_last_week',
}

export interface AdhocTask {
  project: Option;
  activityName: string;
  billable: boolean;
  category?: Option;
  sourceId?: string;
}

export interface AdhockTaskListItem extends AdhocTask {
  selected: boolean;
  disabled: boolean;
}

const non_project_activity_id = 1;

const useProjectOptions = (
  projectList?: Project[],
): { nonProjectActivity: Option<number>; otherProjects: Option<number>[] } => {
  const nonProjectActivityLabel = useTranslation(
    'timesheets.adhoc_activity.non_project_activity',
  );
  const nonProjectActivity = useMemo(
    () =>
      ({
        label: nonProjectActivityLabel,
        value: non_project_activity_id,
        extra: { type: 'all-options' },
      } as Option),
    [nonProjectActivityLabel],
  );

  const otherProjects = useMemo(() => {
    return (projectList ?? [])
      .map((project) => {
        return {
          label: project.projectName,
          value: project.projectId,
          imageUrl: project.customer.companyLogoUrl,
        } as Option;
      })
      .sort((a, b) => naturalCompare(a.label, b.label));
  }, [projectList]);

  return {
    nonProjectActivity,
    otherProjects,
  };
};

export const useProjectOptionsForTimesheet = (
  projectList?: Project[],
): Option[] => {
  const { nonProjectActivity, otherProjects } = useProjectOptions(projectList);
  const { data: configuration } = useTimesheetConfiguration();

  const projectsIncludingNonActivity = useMemo(() => {
    return [nonProjectActivity].concat(otherProjects);
  }, [nonProjectActivity, otherProjects]);

  if (!configuration?.enabledSourceTypes?.ADHOC) {
    return otherProjects;
  }

  return projectsIncludingNonActivity;
};

export const useProjectOptionsForCalendar = (
  projectList?: Project[],
): Option[] => {
  const { nonProjectActivity, otherProjects } = useProjectOptions(projectList);

  const projectsIncludingNonActivity = useMemo(() => {
    return [nonProjectActivity].concat(otherProjects);
  }, [nonProjectActivity, otherProjects]);

  return projectsIncludingNonActivity;
};
export const useCategoryOptions = (
  timeEntryCategories: Category[],
): Option[] => {
  return useMemo(
    () =>
      timeEntryCategories
        .map((category) => ({
          label: category.name,
          value: category.id,
          color: getRandomColor(category.id),
        }))
        .sort((a, b) => naturalCompare(a.label, b.label)),
    [timeEntryCategories],
  );
};

export const useAddAdhocActions = ({
  startDate,
  projects,
  timeEntryCategories,
  alreadyAddedAdhocTasks,
  onAdhocTasksAdd,
}: {
  startDate: Moment;
  projects: Project[];
  alreadyAddedAdhocTasks: TimesheetAdhocTask[];
  timeEntryCategories: Category[];
  onAdhocTasksAdd: (AdhocTask) => void;
}): {
  projectOptions;
  categoryOptions;
  form;
  isAddDisabled;
  adHocTask;
  selectedView;
  setSelectedView;
  isBillableFieldDisabled;
  onFormValueChange;
  onAdd;
  resetValues;
  searchText;
  setSearchText;
  isLoading;
  taskList;
  selectedAllDisplayingTasks;
  onTaskSelected;
  selectedTasks;
  categoryMandatory;
} => {
  const [form] = Form.useForm();
  const [isAddDisabled, setIsAddDisabled] = useState(true);
  const [adHocTask, setAdHocTask] = useState<AdhocTask>();
  const [isBillableFieldDisabled, setIsBillableFieldDisabled] = useState(false);
  const [selectedView, setSelectedView] = useState(View.NewActivity);
  const [searchText, setSearchText] = useState('');
  const [taskList, setTaskList] = useState<AdhockTaskListItem[]>([]);
  const isTaskAlreadyAdded = useCallback(
    (task: AdhocTask) =>
      !!alreadyAddedAdhocTasks.find(
        (x) =>
          x.activityName === task.activityName &&
          (x.project?.id === task.project?.value ||
            (x.project == null &&
              task.project?.value === non_project_activity_id)),
      ),
    [alreadyAddedAdhocTasks],
  );

  const { data: configuration } = useTimesheetConfiguration();
  const { configMeta } = configuration || {};
  const { categoryMandatory } = configMeta || {};
  const projectOptions = useProjectOptionsForTimesheet(projects);
  const NonProjectActivity = projectOptions.find(
    (option) => option.value === non_project_activity_id,
  )!;
  const categoryOptions = useCategoryOptions(timeEntryCategories);

  const isMandatoryAdhocFieldFilled = (task: AdhocTask) =>
    categoryMandatory
      ? Boolean(task.project) &&
        Boolean(task.activityName?.trim()) &&
        Boolean(task.category)
      : Boolean(task.project) && Boolean(task.activityName?.trim());

  const onFormValueChange = (changedValues, allValues) => {
    if (changedValues.project?.value === 1) {
      const modifiedValue = { ...(allValues as AdhocTask), billable: false };

      form.setFieldsValue(modifiedValue);
      setAdHocTask(modifiedValue);
      setIsBillableFieldDisabled(true);
    } else {
      const modifiedValue =
        changedValues.project?.value != null &&
        adHocTask?.project?.value === non_project_activity_id
          ? { ...allValues, billable: true }
          : allValues;

      form.setFieldsValue(modifiedValue);
      setIsBillableFieldDisabled(allValues.project?.value === 1);
      setAdHocTask(modifiedValue);
    }
    setIsAddDisabled(!isMandatoryAdhocFieldFilled(allValues as AdhocTask));
  };

  const {
    user: { userId },
  } = useUser();
  const lastWeekStartDate = useMemo(
    () => startDate.subtract(7, 'days'),
    [startDate],
  );

  const { data: adhocTasks, isLoading } = useQuery(
    ['searchAdhocTasks', lastWeekStartDate.format(DEFAULT_DATE_FORMAT), userId],
    () =>
      TimesheetAPI.searchTimeEntries(
        getSearchFilterForDateAndUser(lastWeekStartDate, userId),
      ),
    {
      enabled: selectedView === View.SubmittedLastWeek,
      staleTime: 0,
      refetchOnWindowFocus: false,
      select: (entries: ApiTimesheetEntry[]) =>
        apiTimeEntriesToAdhocTask(entries, NonProjectActivity),
    },
  );

  const resetValues = useCallback(() => {
    form.resetFields();
    setIsBillableFieldDisabled(false);
    setAdHocTask(undefined);
    setIsAddDisabled(true);
    setTaskList(
      (adhocTasks ?? []).map((task) => {
        const isAlreadyAdded = isTaskAlreadyAdded(task);

        return {
          ...task,
          selected: isAlreadyAdded,
          disabled: isAlreadyAdded,
        };
      }),
    );
  }, [adhocTasks, form, isTaskAlreadyAdded]);

  const selectedTasks = useMemo(
    () => taskList.filter(({ selected, disabled }) => selected && !disabled),
    [taskList],
  );

  const onAdd = () => {
    if (selectedView === View.NewActivity) {
      onAdhocTasksAdd([adHocTask]);
    } else {
      onAdhocTasksAdd(selectedTasks);
    }

    resetValues();
  };

  useEffect(() => {
    if (selectedView === View.SubmittedLastWeek) {
      resetValues();
    } else {
      setTaskList([]);
    }
  }, [adhocTasks, resetValues, selectedView]);

  const displayingTask = useMemo(
    () =>
      taskList.filter(({ activityName }) =>
        activityName.toLowerCase().includes(searchText.toLowerCase()),
      ),
    [searchText, taskList],
  );

  const selectedAllDisplayingTasks = (selectAll: boolean) => {
    if (selectAll) {
      setTaskList(
        taskList.map((item) => {
          if (item.selected) {
            return item;
          }
          if (
            displayingTask.find(
              ({ activityName, project: { value } }) =>
                activityName === item.activityName &&
                value === item.project.value,
            )
          ) {
            return {
              ...item,
              selected: true,
            };
          }

          return item;
        }),
      );
    } else {
      setTaskList(
        taskList.map((item) => {
          if (!item.selected || isTaskAlreadyAdded(item)) {
            return item;
          }
          if (
            displayingTask.find(
              ({ activityName, project: { value } }) =>
                activityName === item.activityName &&
                value === item.project.value,
            )
          ) {
            return {
              ...item,
              selected: false,
            };
          }

          return item;
        }),
      );
    }
  };

  const onTaskSelected = (task: AdhockTaskListItem, selected: boolean) => {
    setTaskList(
      taskList.map((item) => {
        if (
          item.activityName === task.activityName &&
          item.project.value === task.project.value
        ) {
          return {
            ...item,
            selected,
          };
        }

        return item;
      }),
    );
  };

  return {
    projectOptions,
    categoryOptions,
    form,
    isAddDisabled,
    adHocTask,
    isBillableFieldDisabled,
    selectedView,
    searchText,
    setSearchText,
    setSelectedView,
    onFormValueChange,
    onAdd,
    resetValues,
    isLoading,
    taskList: displayingTask,
    selectedAllDisplayingTasks,
    onTaskSelected,
    selectedTasks,
    categoryMandatory,
  };
};
